import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, ImageReveal } from "../components/Reveal";
import { IMG } from "../data/images";

const SERVICES = [
  {
    n: "01",
    title: "Factory Sourcing",
    text: "Finding the right factory changes everything. We identify and evaluate manufacturing partners based on your product, quality expectations, price targets, capacity and MOQ requirements — then shortlist the ones genuinely suited to your scale.",
    points: ["Factory identification & shortlisting", "Capability & capacity evaluation", "MOQ and pricing alignment", "Supplier background checks"],
    img: IMG.footwear,
    alt: "Footwear workshop craftsmanship",
  },
  {
    n: "02",
    title: "Product Development",
    text: "Ideas, references and sketches become commercially viable products here. We translate your vision into specifications factories can actually execute — materials, construction, sampling and iteration included.",
    points: ["Tech packs & specifications", "Material & component sourcing", "Sampling & iterations", "Design-for-manufacture input"],
    img: IMG.development,
    alt: "Hands developing a product sample",
  },
  {
    n: "03",
    title: "Costing & Negotiation",
    text: "Good sourcing protects your margin before production even starts. We build accurate costings, pressure-test quotes and negotiate with suppliers on your behalf — transparently, and always on your side of the table.",
    points: ["Detailed cost breakdowns", "Quote comparison & benchmarking", "Price & term negotiation", "Margin protection"],
    img: IMG.leather,
    alt: "Leather material details",
  },
  {
    n: "04",
    title: "Production Management",
    text: "Once production begins, the real work begins. We manage timelines, approvals, communication and follow-up — so small delays don't become big problems, and you always know where your order stands.",
    points: ["Production timelines & tracking", "Approval management", "Daily supplier communication", "Issue resolution on the ground"],
    img: IMG.production,
    alt: "Production floor work in progress",
  },
  {
    n: "05",
    title: "Quality Control",
    text: "Smaller orders deserve the same standards as large ones. We help ensure your products meet agreed specifications and quality expectations — checking during production, not just at the end.",
    points: ["Specification & standard setting", "In-line production checks", "Pre-shipment inspection", "Quality reporting"],
    img: IMG.bagsDetail,
    alt: "Premium leather bag detail inspection",
  },
  {
    n: "06",
    title: "Shipment Coordination",
    text: "From production completion to final shipment, we support the last mile of the process — documentation, coordination and the hundred small details that decide whether goods arrive on time.",
    points: ["Shipment planning & coordination", "Documentation support", "Freight partner liaison", "Final dispatch follow-up"],
    img: IMG.textiles,
    alt: "Finished materials prepared for dispatch",
  },
];

export default function Services() {
  return (
    <>
      <section data-testid="services-hero" className="bg-ivory pt-36 md:pt-48">
        <div className="mx-auto max-w-7xl px-6 pb-16 md:px-12 md:pb-24">
          <Reveal>
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.3em] text-stone-500">What We Do</p>
          </Reveal>
          <h1 className="mt-8 max-w-4xl font-serif text-5xl leading-[1.05] tracking-tight text-ink md:text-7xl">
            <MaskedLine delay={0.2}>From idea to shipment,</MaskedLine>
            <MaskedLine delay={0.32}>
              we stay <span className="italic text-terra">close to the product.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg">
              Six connected services, one continuous responsibility. Use us for a single stage — or as your
              complete sourcing office in India.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="services-list" className="bg-ivory pb-24 md:pb-36">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          {SERVICES.map((s, i) => (
            <div
              key={s.n}
              data-testid={`service-detail-${s.n}`}
              className={`grid items-center gap-12 border-t border-stone-200 py-16 md:grid-cols-12 md:py-24 ${i % 2 === 1 ? "" : ""}`}
            >
              <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <Reveal>
                  <span className="font-serif text-6xl font-light italic text-stone-300 md:text-7xl">{s.n}</span>
                </Reveal>
                <Reveal delay={0.1}>
                  <h2 className="mt-6 font-serif text-3xl tracking-tight text-ink md:text-5xl">{s.title}</h2>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg">{s.text}</p>
                </Reveal>
                <Reveal delay={0.3}>
                  <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-3 text-sm text-stone-700">
                        <span className="h-1 w-4 bg-terra" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
              <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                <ImageReveal
                  src={s.img}
                  alt={s.alt}
                  className="group aspect-[4/3]"
                  imgClassName="transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                  delay={0.15}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="services-cta" className="noise-overlay bg-ink py-24 text-ivory md:py-32">
        <div className="relative z-10 mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-10 px-6 md:px-12">
          <Reveal>
            <h2 className="max-w-2xl font-serif text-3xl leading-snug tracking-tight md:text-5xl">
              Not sure which stage you need? <span className="italic text-terra-light">Start with the product.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="group inline-flex items-center gap-3 bg-terra px-10 py-5 font-label text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-terra-dark"
            >
              Start a Project
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
